import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBusiness } from "@/context/BusinessContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Search,
  Plus,
  LogOut,
  CheckCircle2,
  ChevronRight,
  X,
} from "lucide-react";
import { toast } from "sonner";

const FEATURE_LABELS = [
  { key: "language_selector",    label: "Language Selector",     hint: "Show a language switcher in the storefront header." },
  { key: "table_bookings",       label: "Table Bookings",        hint: "Customers can reserve a table from the storefront." },
  { key: "print_menu",           label: "Print Professional Menu", hint: "Generate a print-ready PDF of the menu." },
  { key: "import_export_menu",   label: "Import / Export Menu",  hint: "CSV/XLSX import with AI field mapping + export." },
  { key: "holiday_menu",         label: "Holiday Menu",          hint: "Per-date price + availability overrides." },
  { key: "chef_specials",        label: "Chef's Specials",       hint: "Featured-dishes block on the menu page." },
];

const LICENCE_OPTIONS = [
  { value: "unlicensed",   label: "Unlicensed" },
  { value: "byo",          label: "BYO" },
  { value: "licensed",     label: "Licensed" },
  { value: "licensed_byo", label: "Licensed & BYO" },
];

const emptyBusiness = {
  business_name: "", owner_name: "", abn: "", email: "", phone: "", mobile: "",
  address1: "", address2: "", suburb: "", state: "", postcode: "", area: "", main_city: "",
  is_restaurant: true, cuisine: "", sub_cuisine: "", seats: 0, licence: "unlicensed",
  tagline: "", hero_image_url: "", logo_url: "",
  features: {
    language_selector: false, table_bookings: true, print_menu: true,
    import_export_menu: true, holiday_menu: true, chef_specials: true,
  },
};

export default function AgencyControlPanel() {
  const { user, checking } = useAuth();
  const { refresh: refreshBusiness } = useBusiness();
  const navigate = useNavigate();

  const [businesses, setBusinesses] = useState([]);
  const [query, setQuery] = useState("");
  const [cuisines, setCuisines] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // business id OR "new" OR null

  useEffect(() => {
    if (checking) return;
    if (!user || user.role !== "agency_owner") {
      navigate("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const [bs, lk] = await Promise.all([
          api.get("/agency/businesses"),
          api.get("/agency/lookups"),
        ]);
        setBusinesses(bs.data);
        setCuisines(lk.data.cuisines);
        setStates(lk.data.states);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, checking, navigate]);

  // Live fuzzy search against the backend (debounced).
  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/agency/businesses", {
          params: { q: query.trim() },
        });
        setBusinesses(data);
      } catch (_e) { /* ignore */ }
    }, 200);
    return () => clearTimeout(handle);
  }, [query]);

  async function selectBusiness(biz) {
    try {
      await api.post(`/agency/businesses/${biz.id}/select`);
      await refreshBusiness();
      toast.success(`Switched to ${biz.business_name}`);
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  async function toggleFeature(biz, key, value) {
    try {
      const { data } = await api.patch(
        `/agency/businesses/${biz.id}/features`,
        { [key]: value },
      );
      setBusinesses((list) =>
        list.map((b) =>
          b.id === biz.id ? { ...b, features: { ...(b.features || {}), ...data.features } } : b,
        ),
      );
      toast.success(`${key.replace(/_/g, " ")}: ${value ? "on" : "off"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div data-testid="agency-control-panel" className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b border-[#E8E5DF] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-[#C05746]" strokeWidth={1.5} />
            <span className="font-serif-display text-xl">Agency Control Panel</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-[#5C625E]">
            <Link
              to="/agency/loyalty-pool"
              data-testid="agency-header-loyalty-pool-link"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1 text-xs font-medium hover:bg-amber-100"
            >
              ◎ Loyalty pool
            </Link>
            <Link
              to="/agency/settings"
              data-testid="agency-header-settings-link"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200 px-3 py-1 text-xs font-medium hover:bg-sky-100"
            >
              ✎ Agency Settings
            </Link>
            <Link
              to="/agency/console"
              data-testid="agency-header-console-link"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#C05746] text-white border border-[#A64A3C] px-3 py-1 text-xs font-medium hover:bg-[#A64A3C]"
            >
              ⌘ Agency Console
            </Link>
            <Link
              to="/agency/hc-settings"
              data-testid="agency-header-hc-settings-link"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-violet-50 text-violet-800 border border-violet-200 px-3 py-1 text-xs font-medium hover:bg-violet-100"
            >
              ⚙ HC settings
            </Link>
            <Link
              to="/agency/claims"
              data-testid="agency-header-claims-link"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-medium hover:bg-emerald-100"
            >
              📊 Claim insights
            </Link>
            <Link
              to="/agency/import"
              data-testid="agency-header-import-link"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#121614] text-white px-3 py-1 text-xs font-medium hover:opacity-90"
            >
              ⬆ Bulk import
            </Link>
            <span>{user?.email}</span>
            <button
              type="button"
              data-testid="agency-logout"
              onClick={async () => {
                await api.post("/auth/logout");
                navigate("/admin/login", { replace: true });
              }}
              className="inline-flex items-center gap-1 hover:text-[#C05746]"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
              Businesses
            </p>
            <h1 className="font-serif-display text-4xl sm:text-5xl mt-2">
              Pick a business to manage
            </h1>
            <p className="text-[#5C625E] mt-2">
              Search by name, postcode, suburb, city, phone, or email. Spelling-tolerant.
            </p>
          </div>
          <Button
            type="button"
            data-testid="agency-new-business"
            onClick={() => setEditing("new")}
            className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full h-11 px-5"
          >
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Add business
          </Button>
        </div>

        <div className="mt-6 relative max-w-2xl">
          <Search className="absolute left-4 top-3.5 h-4 w-4 text-[#5C625E]" strokeWidth={1.5} />
          <Input
            data-testid="agency-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by business name, postcode, suburb, phone…"
            className="pl-11 h-12 rounded-full bg-white border-[#E8E5DF]"
          />
          {query && (
            <button
              type="button"
              data-testid="agency-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-4 top-3.5 text-[#5C625E] hover:text-[#C05746]"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          )}
        </div>

        {loading ? (
          <p className="mt-10 text-[#5C625E]">Loading…</p>
        ) : businesses.length === 0 ? (
          <div data-testid="agency-empty" className="mt-10 bg-white border border-[#E8E5DF] rounded-2xl p-10 text-center">
            <p className="font-serif-display text-2xl">No matches</p>
            <p className="text-sm text-[#5C625E] mt-2">
              Try a different search, or add a new business.
            </p>
          </div>
        ) : (
          <ul data-testid="agency-business-list" className="mt-8 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {businesses.map((b) => (
              <li
                key={b.id}
                data-testid={`agency-business-${b.id}`}
                className="bg-white border border-[#E8E5DF] rounded-2xl p-5 hover:border-[#C05746] hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-serif-display text-2xl truncate">{b.business_name}</h3>
                    <p className="text-xs text-[#5C625E] mt-1 truncate">
                      {b.cuisine && cuisines.find((c) => c.code === b.cuisine)?.label}
                      {b.sub_cuisine && <> · {b.sub_cuisine}</>}
                    </p>
                    <p className="text-xs text-[#5C625E] mt-2">
                      {[b.suburb, b.state, b.postcode].filter(Boolean).join(", ")}
                    </p>
                    {b.phone && (
                      <p className="text-xs text-[#5C625E] mt-1">{b.phone}</p>
                    )}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 border-t border-[#F3F0EA] pt-3">
                  <label
                    className="flex items-center justify-between text-xs gap-2"
                    data-testid={`agency-feat-row-table_bookings-${b.id}`}
                  >
                    <span className="text-[#5C625E]">Book a table</span>
                    <Switch
                      data-testid={`agency-feat-toggle-table_bookings-${b.id}`}
                      checked={b.features?.table_bookings !== false}
                      onCheckedChange={(v) => toggleFeature(b, "table_bookings", v)}
                    />
                  </label>
                  <label
                    className="flex items-center justify-between text-xs gap-2"
                    data-testid={`agency-feat-row-language_selector-${b.id}`}
                  >
                    <span className="text-[#5C625E]">Languages</span>
                    <Switch
                      data-testid={`agency-feat-toggle-language_selector-${b.id}`}
                      checked={!!b.features?.language_selector}
                      onCheckedChange={(v) => toggleFeature(b, "language_selector", v)}
                    />
                  </label>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    type="button"
                    data-testid={`agency-select-${b.id}`}
                    aria-label={`Manage ${b.business_name}`}
                    onClick={() => selectBusiness(b)}
                    className="flex-1 bg-[#5A7247] hover:bg-[#46583a] text-white rounded-full h-9 text-xs"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                    Manage
                  </Button>
                  <Button
                    type="button"
                    data-testid={`agency-edit-${b.id}`}
                    aria-label={`Edit ${b.business_name}`}
                    onClick={() => setEditing(b.id)}
                    variant="outline"
                    className="rounded-full h-9 text-xs border-[#E8E5DF]"
                  >
                    Edit
                    <ChevronRight className="h-3.5 w-3.5 ml-1" strokeWidth={1.5} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <VendorUsersSection businesses={businesses} />
      </main>

      {editing && (
        <BusinessEditor
          bizId={editing === "new" ? null : editing}
          cuisines={cuisines}
          states={states}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            const { data } = await api.get("/agency/businesses");
            setBusinesses(data);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function BusinessEditor({ bizId, cuisines, states, onClose, onSaved }) {
  const [form, setForm] = useState(emptyBusiness);
  const [loading, setLoading] = useState(!!bizId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!bizId) return;
    (async () => {
      try {
        const { data } = await api.get(`/agency/businesses/${bizId}`);
        setForm({ ...emptyBusiness, ...data, features: { ...emptyBusiness.features, ...(data.features || {}) } });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [bizId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFeature = (k, v) => setForm((f) => ({ ...f, features: { ...f.features, [k]: v } }));

  const subCuisines = useMemo(() => {
    const c = cuisines.find((x) => x.code === form.cuisine);
    return c ? c.sub : [];
  }, [cuisines, form.cuisine]);

  async function save() {
    if (!form.business_name) return toast.error("Business name required");
    if (!form.email) return toast.error("Business email required");
    setSaving(true);
    try {
      const payload = { ...form, seats: parseInt(form.seats) || 0 };
      if (bizId) {
        await api.put(`/agency/businesses/${bizId}`, payload);
      } else {
        await api.post("/agency/businesses", payload);
      }
      toast.success("Business saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  return (
    <div
      data-testid="business-editor"
      className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
    >
      <div className="bg-white w-full max-w-3xl rounded-none sm:rounded-2xl min-h-screen sm:min-h-0 my-0 sm:my-8">
        <div className="sticky top-0 bg-white border-b border-[#E8E5DF] px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="font-serif-display text-2xl">
            {bizId ? "Edit business" : "Add a business"}
          </h2>
          <button
            type="button"
            data-testid="business-editor-close"
            onClick={onClose}
            className="text-[#5C625E] hover:text-[#C05746]"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <Section title="Owner & contact">
            <Field label="Business name *">
              <Input data-testid="biz-business-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />
            </Field>
            <Field label="Owner name">
              <Input data-testid="biz-owner-name" value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} />
            </Field>
            <Field label="ABN">
              <Input data-testid="biz-abn" value={form.abn} onChange={(e) => set("abn", e.target.value)} />
            </Field>
            <Field label="Business email *">
              <Input data-testid="biz-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </Field>
            <Field label="Phone">
              <Input data-testid="biz-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </Field>
            <Field label="Mobile">
              <Input data-testid="biz-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} />
            </Field>
          </Section>

          <Section title="Address">
            <Field label="Address line 1">
              <Input data-testid="biz-addr1" value={form.address1} onChange={(e) => set("address1", e.target.value)} />
            </Field>
            <Field label="Address line 2">
              <Input data-testid="biz-addr2" value={form.address2} onChange={(e) => set("address2", e.target.value)} />
            </Field>
            <Field label="Suburb">
              <Input data-testid="biz-suburb" value={form.suburb} onChange={(e) => set("suburb", e.target.value)} />
            </Field>
            <Field label="State">
              <Select value={form.state} onValueChange={(v) => set("state", v)}>
                <SelectTrigger data-testid="biz-state" className="rounded-xl"><SelectValue placeholder="State…" /></SelectTrigger>
                <SelectContent>
                  {states.map((s) => (
                    <SelectItem key={s.code} value={s.code}>{s.code} · {s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Postcode">
              <Input data-testid="biz-postcode" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} />
            </Field>
            <Field label="Area (e.g. SEQ)">
              <Input data-testid="biz-area" value={form.area} onChange={(e) => set("area", e.target.value)} />
            </Field>
            <Field label="Main city (used for public-holiday lookups)">
              <Input data-testid="biz-main-city" value={form.main_city} onChange={(e) => set("main_city", e.target.value)} />
            </Field>
          </Section>

          <Section title="Classification">
            <Field label="Cuisine">
              <Select value={form.cuisine} onValueChange={(v) => { set("cuisine", v); set("sub_cuisine", ""); }}>
                <SelectTrigger data-testid="biz-cuisine" className="rounded-xl"><SelectValue placeholder="Pick a cuisine…" /></SelectTrigger>
                <SelectContent>
                  {cuisines.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sub-cuisine">
              <Select value={form.sub_cuisine} onValueChange={(v) => set("sub_cuisine", v)} disabled={!subCuisines.length}>
                <SelectTrigger data-testid="biz-sub-cuisine" className="rounded-xl">
                  <SelectValue placeholder={subCuisines.length ? "Pick a sub-cuisine…" : "Pick cuisine first"} />
                </SelectTrigger>
                <SelectContent>
                  {subCuisines.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Number of seats">
              <Input data-testid="biz-seats" type="number" min="0" max="2000" value={form.seats} onChange={(e) => set("seats", e.target.value)} />
            </Field>
            <Field label="Licensing">
              <Select value={form.licence} onValueChange={(v) => set("licence", v)}>
                <SelectTrigger data-testid="biz-licence" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LICENCE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </Section>

          <Section title="Storefront features">
            <div className="col-span-full grid sm:grid-cols-2 gap-3">
              {FEATURE_LABELS.map((f) => (
                <label
                  key={f.key}
                  className="flex items-start justify-between gap-3 border border-[#E8E5DF] rounded-xl px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{f.label}</p>
                    <p className="text-xs text-[#5C625E] mt-0.5">{f.hint}</p>
                  </div>
                  <Switch
                    data-testid={`biz-feat-${f.key}`}
                    checked={!!form.features[f.key]}
                    onCheckedChange={(v) => setFeature(f.key, v)}
                  />
                </label>
              ))}
            </div>
          </Section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E8E5DF] px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            type="button"
            data-testid="business-editor-save"
            onClick={save}
            disabled={saving}
            className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6"
          >
            {saving ? "Saving…" : "Save business"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247] mb-3">{title}</p>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function VendorUsersSection({ businesses }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    email: "", password: "", name: "", business_id: "",
  });
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/agency/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function createVendor() {
    if (!draft.email || !draft.password || !draft.business_id) {
      toast.error("Email, password and business are required.");
      return;
    }
    if (draft.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/agency/users", draft);
      toast.success("Vendor created.");
      setDraft({ email: "", password: "", name: "", business_id: "" });
      setCreating(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeVendor(email) {
    if (!window.confirm(`Remove ${email}? They will lose all access immediately.`)) return;
    try {
      await api.delete(`/agency/users/${encodeURIComponent(email)}`);
      toast.success("Vendor removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  const bizMap = Object.fromEntries((businesses || []).map((b) => [b.id, b.business_name]));

  return (
    <section
      data-testid="agency-users-section"
      className="mt-12 bg-white border border-[#E8E5DF] rounded-2xl p-6"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247]">
            Staff & Vendors
          </p>
          <h2 className="font-serif-display text-2xl mt-1">Who can log in</h2>
          <p className="text-xs text-[#5C625E] mt-1">
            Agency owners see every business. Vendors are locked to one business
            and never see the Agency panel or tenant switcher.
          </p>
        </div>
        <Button
          type="button"
          data-testid="agency-add-vendor"
          onClick={() => setCreating(true)}
          className="bg-[#5A7247] hover:bg-[#46583a] text-white rounded-full"
        >
          <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
          Add vendor
        </Button>
      </div>

      <div className="mt-5">
        {loading ? (
          <p className="text-[#5C625E] text-sm">Loading users…</p>
        ) : users.length === 0 ? (
          <p className="text-[#5C625E] text-sm">No staff yet.</p>
        ) : (
          <ul data-testid="agency-users-list" className="divide-y divide-[#F3F0EA]">
            {users.map((u) => (
              <li
                key={u.email}
                data-testid={`agency-user-${u.email}`}
                className="py-3 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{u.name || u.email}</p>
                  <p className="text-xs text-[#5C625E] truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${
                      u.role === "agency_owner"
                        ? "bg-[#C05746]/10 text-[#C05746]"
                        : "bg-[#5A7247]/10 text-[#5A7247]"
                    }`}
                  >
                    {u.role === "agency_owner" ? "Agency" : "Vendor"}
                  </span>
                  {u.role === "business_admin" && (
                    <>
                      <span className="text-xs text-[#5C625E] truncate max-w-[160px]">
                        {bizMap[u.business_id] || "—"}
                      </span>
                      <button
                        type="button"
                        data-testid={`agency-user-remove-${u.email}`}
                        onClick={() => removeVendor(u.email)}
                        className="text-red-600 hover:text-red-700 text-xs hover:underline"
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {creating && (
        <div
          data-testid="agency-vendor-dialog"
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setCreating(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif-display text-2xl">New vendor</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  data-testid="vendor-form-email"
                  type="email"
                  value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                  className="mt-1 rounded-lg"
                  placeholder="owner@theirvenue.com"
                />
              </div>
              <div>
                <Label className="text-xs">Display name</Label>
                <Input
                  data-testid="vendor-form-name"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="mt-1 rounded-lg"
                />
              </div>
              <div>
                <Label className="text-xs">Temporary password (min 8 chars)</Label>
                <Input
                  data-testid="vendor-form-password"
                  type="password"
                  value={draft.password}
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                  className="mt-1 rounded-lg"
                />
              </div>
              <div>
                <Label className="text-xs">Business they manage</Label>
                <Select
                  value={draft.business_id}
                  onValueChange={(v) => setDraft({ ...draft, business_id: v })}
                >
                  <SelectTrigger
                    data-testid="vendor-form-business"
                    className="mt-1 rounded-lg"
                  >
                    <SelectValue placeholder="Select a business" />
                  </SelectTrigger>
                  <SelectContent>
                    {(businesses || []).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.business_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreating(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="vendor-form-save"
                onClick={createVendor}
                disabled={saving}
                className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full"
              >
                {saving ? "Creating…" : "Create vendor"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
